import axios from 'axios';

// sessionId vigente de este navegador (se actualiza en login Y en refresh).
// No confundir con `globalSessionId`, que solo se escribe en login para avisar
// a las pestañas hermanas que se inició una sesión nueva (ver listener
// 'storage' en App.jsx): si el refresh lo escribiera, cada renovación
// cerraría las demás pestañas del mismo navegador.
const AUTH_SESSION_KEY = 'authSessionId';
const REFRESH_LOCK_NAME = 'signly-auth-refresh';
// Si al obtener el lock el token ya tiene más de esto de vida, otra pestaña
// acaba de renovarlo y no hace falta volver a llamar a /auth/refresh.
const FRESH_TOKEN_MIN_SECONDS = 300;

// Motivos para el aviso que muestra Login2 tras un cierre de sesión forzado.
export const SESSION_ALERT_REASONS = {
  SAME_BROWSER: 'true', // valor histórico: otra pestaña de este navegador inició sesión
  OTHER_DEVICE: 'other_device',
  EXPIRED: 'expired'
};

export const getCurrentSessionId = () =>
  localStorage.getItem(AUTH_SESSION_KEY) || localStorage.getItem('globalSessionId');

export const setCurrentSessionId = (sessionId) => {
  if (sessionId) {
    localStorage.setItem(AUTH_SESSION_KEY, sessionId);
  } else {
    localStorage.removeItem(AUTH_SESSION_KEY);
  }
};

// Serializa el refresh entre TODAS las pestañas del navegador. Sin esto, dos
// pestañas que refrescan a la vez (p. ej. al despertar la PC) mandan la misma
// cookie: la primera rota el token en BD y la segunda recibe 403, cerrando una
// sesión que sí era válida.
export const withRefreshLock = (fn) => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request(REFRESH_LOCK_NAME, fn);
  }
  return fn();
};

// Renueva la sesión y guarda tokenExp/sessionId. Devuelve { exp, sessionId }.
// Lanza el error de axios si el backend rechaza el refresh.
export const requestTokenRefresh = () =>
  withRefreshLock(async () => {
    const currentExp = Number(localStorage.getItem('tokenExp'));
    if (currentExp && currentExp - Date.now() / 1000 > FRESH_TOKEN_MIN_SECONDS) {
      return { exp: currentExp, sessionId: getCurrentSessionId(), reused: true };
    }

    const { data } = await axios.get(
      `${process.env.REACT_APP_BASE_URL}auth/refresh`,
      { withCredentials: true }
    );
    if (data.exp) {
      localStorage.setItem('tokenExp', data.exp);
      setCurrentSessionId(data.sessionId);
    }
    return data;
  });

// Motivo del aviso según la respuesta 403 de /auth/refresh. Un backend sin
// `code` (versión anterior) solo devolvía 403 por token rotado.
export const getRefreshFailureReason = (err) =>
  err?.response?.data?.code === 'REFRESH_EXPIRED'
    ? SESSION_ALERT_REASONS.EXPIRED
    : SESSION_ALERT_REASONS.OTHER_DEVICE;
